// import TRTC from "trtc-js-sdk";

import TRTCCloud, {
  TRTCParams,
  TRTCRoleType,
  TRTCAppScene,
  TRTCScreenCaptureProperty,
  TRTCVideoResolution,
  TRTCVideoResolutionMode,
  TRTCVideoEncParam,
  TRTCVideoStreamType,
  Rect,
  TRTCRenderParams,
  TRTCVideoRotation,
  TRTCVideoFillMode,
  TRTCVideoMirrorType,
  TRTCBeautyStyle,
  TRTCAudioQuality,
  TRTCDeviceState,
} from "trtc-electron-sdk";
import { BaseLiveService } from "../base";
import { EMIT_MAP } from "../constants";
import store from "@saas/store/src";
import { createQueueAsyncFn } from "../../utils/debounce-async";
import { uploadLiveLog } from "@saas/components/src/utils/log";

// 进房相关错误码
const ENTER_ROOM_ERROR_MAP = {
  [-3301]: "进入房间失败",
  [-3316]: "进房参数为空，请检查是否传入有效的 param",
  [-3317]: "进房参数 sdkAppId 错误",
  [-3318]: "进房参数 roomId 错误",
  [-3319]: "进房参数 userId 不正确",
  [-3320]: "进房参数 userSig 不正确",
  [-3308]: "请求进房超时，请检查网络",
  [-100006]: "权限位校验失败，请检查 privateMapKey 是否正确",
  [-100013]: "服务不可用，请检查套餐包剩余分钟数或账号欠费情况",
  [-100018]: "userSig 校验失败，请检查 userSig 是否正确",
};

// 退房相关错误码
const EXIT_ROOM_ERROR_MAP = {
  [-3325]: "请求退房超时",
};

// 摄像头相关错误码
const CAMERA_ERROR_MAP = {
  [-1301]: "打开摄像头失败，可能是驱动异常或设备被禁用",
  [-1314]: "摄像头未授权，可能被用户拒绝权限",
  [-1315]: "摄像头参数设置出错",
  [-1316]: "摄像头正在被占用",
};

// 麦克风相关错误码
const MICROPHONE_ERROR_MAP = {
  [-1302]: "打开麦克风失败，可能是驱动异常或设备被禁用",
  [-1317]: "麦克风未授权，可能被用户拒绝权限",
  [-1318]: "麦克风参数设置失败",
  [-1319]: "麦克风正在被占用",
  [-1320]: "停止麦克风失败",
};

// 扬声器相关错误码
const SPEAKER_ERROR_MAP = {
  [-1321]: "打开扬声器失败，可能是驱动异常或设备被禁用",
  [-1322]: "扬声器参数设置失败",
  [-1323]: "停止扬声器失败",
};

// 屏幕共享相关错误码
const SCREEN_SHARE_ERROR_MAP = {
  [-1308]: "开始录屏失败，可能权限被拒绝或参数不符合要求",
  [-1309]: "录屏失败，系统版本暂不支持",
  [-102015]: "没有权限上行辅路",
  [-102016]: "其他用户正在上行辅路",
  [-7001]: "录屏被系统中止",
};

// 自定义采集相关错误码
const CUSTOM_CAPTURE_ERROR_MAP = {
  [-1327]: "自定义采集像素格式不支持",
  [-1328]: "自定义采集缓冲类型不支持",
};

// 编解码相关错误码
const CODEC_ERROR_MAP = {
  [-1303]: "视频帧编码失败",
  [-1304]: "音频帧编码失败",
  [-1305]: "不支持的视频分辨率",
  [-1306]: "不支持的音频采样率",
};

// 麦克风错误码
class TRTCLiveService extends BaseLiveService {
  constructor(options) {
    super();
    this.options = options;
    this.client = TRTCCloud.getTRTCShareInstance();
    this.client.setDefaultStreamRecvMode(false, false);
    // 初始化队列异步执行函数
    this.queueAsyncVideo = createQueueAsyncFn();
    this.queueAsyncVideoTestClient = createQueueAsyncFn();
    this.queueAsyncAudio = createQueueAsyncFn();
    this.audioState = false; // 本地音频开启状态 调用了startLocalAudio后为true 调用了stopLocalAudio后为false
    this.videoState = false; // 本地视频开启状态 调用了startLocalPreview后为true 调用了stopLocalPreview后为false
    this.enableAudioVolumeEvaluation(); // 是否开启音频音量评估
    if (!this.options.isRecord) {
      this.initStartLocalAudio();
    }
    this.isEnterRoomSuccess = false;
    this.registerListener();
    this.testClient = this.client.createSubCloud();
    this.renderCameraDom = null;
    this.screenShareProfile = {
      resolution: TRTCVideoResolution.TRTCVideoResolution_1280_720,
      bitrate: 1200,
      minBitrate: 600,
      fps: 15,
    };
    this.videoProfile = {
      resolution: TRTCVideoResolution.TRTCVideoResolution_320_180,
      bitrate: 400,
      minBitrate: 200,
      fps: 15,
    };
    this.setVideoEncoderParams();
  }
  registerListener() {
    this.client.on("onError", this.onError.bind(this));
    this.client.on("onWarning", this.onWarning.bind(this));
    this.client.on("onEnterRoom", this.onEnterRoom.bind(this));
    this.client.on(
      "onUserVideoAvailable",
      this.onUserVideoAvailable.bind(this)
    );
    this.client.on(
      "onUserSubStreamAvailable",
      this.onUserSubStreamAvailable.bind(this)
    );
    this.client.on(
      "onUserAudioAvailable",
      this.onUserAudioAvailable.bind(this)
    );
    this.client.on(
      "onRemoteUserEnterRoom",
      this.onRemoteUserEnterRoom.bind(this)
    );
    this.client.on(
      "onRemoteUserLeaveRoom",
      this.onRemoteUserLeaveRoom.bind(this)
    );
    this.client.on("onTestMicVolume", this.onTestMicVolume.bind(this));
    this.client.on("onNetworkQuality", this.onNetworkQuality.bind(this));
    this.client.on("onStatistics", this.onStatistics.bind(this));
    this.client.on("onRecvCustomCmdMsg", this.onRecvCustomCmdMsg.bind(this));
    this.client.on("onMissCustomCmdMsg", this.onMissCustomCmdMsg.bind(this));
    this.client.on("onUserVoiceVolume", this.onUserVoiceVolume.bind(this));
    this.client.on("onExitRoom", this.onExitRoom.bind(this));
    this.client.on("onDeviceChange", this.onDeviceChange.bind(this));
    this.client.on(
      "onScreenCaptureStopped",
      this.onScreenCaptureStopped.bind(this)
    );
    this.client.on(
      "onConnectionRecovery",
      this.onConnectionRecovery.bind(this)
    );
    this.client.on("onConnectionLost", this.onConnectionLost.bind(this));
    this.client.on("onTryToReconnect", this.onTryToReconnect.bind(this));
  }
  onConnectionRecovery(event) {
    console.log("onConnectionRecovery>>>", event);
    this._emit(EMIT_MAP.onConnectionRecovery);
    uploadLiveLog({
      message: "Electron-TRTC连接恢复",
      level: "info",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
      },
    });
  }
  onConnectionLost(event) {
    console.log("onConnectionLost>>>", event);
    this._emit(EMIT_MAP.onConnectionLost);
    uploadLiveLog({
      message: "Electron-TRTC连接断开",
      level: "error",
      tag: "callback",
      extra: { component: "ElectronTRTCService" },
    });
  }
  onTryToReconnect(event) {
    console.log("onTryToReconnect>>>", event);
    this._emit(EMIT_MAP.onTryToReconnect);
    uploadLiveLog({
      message: "Electron-TRTC尝试重连",
      level: "warn",
      tag: "callback",
      extra: { component: "ElectronTRTCService" },
    });
  }
  onNetworkQuality(localQuality, remoteQuality) {
    this._emit(EMIT_MAP.onNetworkQuality, {
      localQuality,
      remoteQuality,
    });
  }
  onStatistics(data) {
    const res = {
      upLoss: data.upLoss,
      downLoss: data.downLoss,
      appCpu: data.appCpu,
      systemCpu: data.systemCpu,
      rtt: data.rtt,
      gatewayRtt: data.gatewayRtt,
      // receivedBytes: data.receivedBytes,
      // sentBytes: data.sentBytes,
      // localStatisticsArray: data.localStatisticsArray,
      // localStatisticsArraySize: data.localStatisticsArraySize,
      // remoteStatisticsArray: data.remoteStatisticsArray,
      // remoteStatisticsArraySize: data.remoteStatisticsArraySize,
      // systemMemoryInMB: data.systemMemoryInMB,
      // systemMemoryUsageInMB: data.systemMemoryUsageInMB,
      // appMemoryUsageInMB: data.appMemoryUsageInMB,
    };
    this._emit(EMIT_MAP.onStatistics, res);
  }
  onError(errCode, errMsg) {
    const err = {
      errCode,
      errMsg,
    };
    this._emit(EMIT_MAP.onError, err);
    if (ENTER_ROOM_ERROR_MAP[errCode]) {
      // 进房失败
      this._emit(EMIT_MAP.onEnterRoomFail, {
        errCode: errCode,
        errMsg: "进入直播间失败",
        errType: "enterRoom",
      });
    } else if (CAMERA_ERROR_MAP[errCode]) {
      // 摄像头错误
      this._emit(EMIT_MAP.onCameraError, {
        errCode: errCode,
        errMsg: "“摄像头”异常",
        errType: "camera",
      });
    } else if (MICROPHONE_ERROR_MAP[errCode]) {
      // 麦克风错误
      this._emit(EMIT_MAP.onMicrophoneError, {
        errCode: errCode,
        errMsg: "“麦克风”异常",
        errType: "microphone",
      });
    } else if (SPEAKER_ERROR_MAP[errCode]) {
      // 扬声器错误
      this._emit(EMIT_MAP.onSpeakerError, {
        errCode: errCode,
        errMsg: "“扬声器”异常",
        errType: "speaker",
      });
    } else if (SCREEN_SHARE_ERROR_MAP[errCode]) {
      // 录屏错误
      this._emit(EMIT_MAP.onScreenShareError, {
        errCode: errCode,
        errMsg: "屏幕分享失败",
        errType: "screenShare",
      });
    } else if (CUSTOM_CAPTURE_ERROR_MAP[errCode]) {
      // 自定义采集错误
      this._emit(EMIT_MAP.onCustomCaptureError, {
        errCode: errCode,
        errMsg: "自定义采集失败",
        errType: "customCapture",
      });
    } else if (CODEC_ERROR_MAP[errCode]) {
      // 编解码错误
      this._emit(EMIT_MAP.onCodecError, {
        errCode: errCode,
        errMsg: "音视频解码失败",
        errType: "codec",
      });
    }
    uploadLiveLog({
      message: "Electron-TRTC错误",
      level: "error",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
        error: err,
      },
    });
  }
  onWarning(err) {
    this._emit(EMIT_MAP.onWarning, err);
    uploadLiveLog({
      message: "Electron-TRTC警告",
      level: "warn",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
        warning: err,
      },
    });
  }
  onEnterRoom(result) {
    if (result > 0) {
      this.isEnterRoomSuccess = true;
      this._emit(EMIT_MAP.onEnterRoomSuccess);
      uploadLiveLog({
        message: "Electron-TRTC进房成功",
        level: "info",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          costTime: result,
        },
      });
    } else {
      this.isEnterRoomSuccess = false;
      this._emit(EMIT_MAP.onEnterRoomFail, {
        errCode: result,
        errMsg: "进入直播间失败",
        errType: "enterRoom",
      });
      uploadLiveLog({
        message: "Electron-TRTC进房失败",
        level: "error",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          error: result,
          errorCode: result,
        },
      });
      // 参见进房错误码 https://cloud.tencent.com/document/product/647/32257#.E8.BF.9B.E6.88.BF.E7.9B.B8.E5.85.B3.E9.94.99.E8.AF.AF.E7.A0.81
    }
  }
  //有用户加入当前房间 直播场景（TRTCAppSceneLIVE 和 TRTCAppSceneVoiceChatRoom）：该场景不限制观众的数量，如果任何用户进出都抛出回调会引起很大的性能损耗，所以该场景下只有主播进入房间时才会触发该通知，观众进入房间不会触发该通知。
  onRemoteUserEnterRoom(userID) {
    console.log("onRemoteUserEnterRoom>>>>>>", userID);
    this._emit(EMIT_MAP.onRemoteUserEnterRoom, { userID });
    uploadLiveLog({
      message: "Electron-TRTC有用户加入当前房间",
      level: "info",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
        userId: userID,
      },
    });
  }
  onRemoteUserLeaveRoom(userID, reason) {
    console.log("onRemoteUserLeaveRoom>>>>>>", userID);
    console.log("onRemoteUserLeaveRoom>>>>>>", reason);
    this._emit(EMIT_MAP.onRemoteUserLeaveRoom, {
      userID: userID,
      reason: reason,
    });
    uploadLiveLog({
      message: "Electron-TRTC有用户离开当前房间",
      level: "info",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
        userId: userID,
      },
    });
  }
  onTestMicVolume(volume) {
    this._emit(EMIT_MAP.onTestMicVolume, volume);
  }
  onUserVoiceVolume(params) {
    // console.log("onUserVoiceVolume>>>>>>", params);

    // const userVolumeList = params.userVolumes || [];
    params.forEach((user) => {
      this._emit(EMIT_MAP.onAudioVolumnChange, {
        userId: user.userId,
        volume: user.volume,
      });
    });
  }
  //
  onUserVideoAvailable(userID, available) {
    this._emit(EMIT_MAP.onUserVideoAvailable, {
      userID: userID,
      available: available, // 0隐藏，1显示
    });
    uploadLiveLog({
      message: "Electron-TRTC用户视频流状态变化",
      level: "info",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
        userId: userID,
        available: available,
      },
    });
  }
  onUserSubStreamAvailable(userID, available) {
    this._emit(EMIT_MAP.onUserSubStreamAvailable, {
      userID: userID,
      available: available, // 0隐藏，1显示
    });
    uploadLiveLog({
      message: "Electron-TRTC用户子流状态变化",
      level: "info",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
        userId: userID,
        available: available,
      },
    });
  }
  onUserAudioAvailable(userID, available) {
    this._emit(EMIT_MAP.onUserAudioAvailable, {
      userID,
      available, // 0隐藏，1显示
    });
    uploadLiveLog({
      message: "Electron-TRTC用户音频流状态变化",
      level: "info",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
        userId: userID,
        available: available,
      },
    });
  }
  onMissCustomCmdMsg(res) {
    console.log("==55onMissCustomMessage>>>>>>", res);
  }
  onRecvCustomCmdMsg(userId, cmdId, seq, msg) {
    console.log("==55onRecvCustomMessage>>>>>>", userId, cmdId, seq, msg);
    const decoder = new TextDecoder();
    let data = decoder.decode(msg);
    data = JSON.parse(data);
    console.log("data111", data);
    if (typeof data === "string") {
      console.log(888);
      data = JSON.parse(data);
    }
    if (typeof data === "string") {
      console.log(999);

      data = JSON.parse(data);
    }
    if (typeof data === "string") {
      console.log(111);

      data = JSON.parse(data);
    }
    this._emit(EMIT_MAP.onRecvCustomCmdMsg, {
      userId: userId,
      data: data,
      cmdId: cmdId,
      seq: seq,
    });
  }
  onExitRoom(reason) {
    // 离开房间原因，0：主动调用 exitRoom 退房；1：被服务器踢出当前房间；2：当前房间整个被解散。
    console.log("electron_exitroom", reason);
    const map = {
      0: "exit",
      1: "banned",
      2: "room_disband",
    };
    this._emit(EMIT_MAP.onKickedOut, map[reason]);
    uploadLiveLog({
      message: "Electron-TRTC离开房间",
      level: "info",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
        reason: map[reason],
      },
    });
  }
  onDeviceChange(deviceId, type, state) {
    console.log("device_electron_onDeviceChange__", deviceId, type, state);
    // -1 未知， 0 麦克风 1 扬声器 2 摄像头
    const deviceTypeMap = {
      "-1": "unknown",
      0: "microphone",
      1: "speaker",
      2: "camera",
    };

    const deviceType = deviceTypeMap[type];

    if (state === TRTCDeviceState.TRTCDefaultDeviceChanged) {
      // 系统默认设备变更
      this._emit(EMIT_MAP.onDefaultDeviceChange, {
        deviceId: deviceId,
        type: deviceType,
      });
      uploadLiveLog({
        message: `Electron-TRTC默认设备变更`,
        level: "info",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          deviceId,
          deviceType,
          state: "default_changed",
        },
      });
    } else if (
      [
        TRTCDeviceState.TRTCDeviceStateAdd,
        TRTCDeviceState.TRTCDeviceStateRemove,
      ].includes(state)
    ) {
      // 设备添加或者移除
      const deviceState =
        state === TRTCDeviceState.TRTCDeviceStateAdd ? "add" : "remove";
      this._emit(EMIT_MAP.onDeviceChange, {
        type: deviceType,
        deviceId: deviceId,
        state: deviceState,
      });
      uploadLiveLog({
        message: `Electron-TRTC设备${deviceState === "add" ? "添加" : "移除"}`,
        level: "info",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          deviceId,
          deviceType,
          state: deviceState,
        },
      });
    } else if (TRTCDeviceState.TRTCDeviceStateActive === state) {
      // 设备启动
      this._emit(EMIT_MAP.onDeviceActive, {
        deviceId: deviceId,
        type: deviceType,
      });
      uploadLiveLog({
        message: `Electron-TRTC设备启动`,
        level: "info",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          deviceId,
          deviceType,
          state: "active",
        },
      });
    }
  }
  onScreenCaptureStopped() {
    this._emit(EMIT_MAP.onScreenShareStop);
    uploadLiveLog({
      message: "Electron-TRTC屏幕共享停止",
      level: "info",
      tag: "callback",
      extra: {
        component: "ElectronTRTCService",
      },
    });
  }
  enterRoom() {
    return new Promise((resolve, reject) => {
      if (this.client) {
        // console.log("enterRoom", this.options);
        const param = new TRTCParams();
        param.sdkAppId = this.options.sdkAppId;
        param.userId = this.options.userId;
        param.strRoomId = this.options.roomId + "";
        param.userSig = this.options.userSig;
        param.role = TRTCRoleType.TRTCRoleAnchor;
        param.privateMapKey = this.options.privateMapKey;
        // 如果您的场景是"在线直播"，请将应用场景设置为 TRTC_APP_SCENE_LIVE
        this.client.enterRoom(param, TRTCAppScene.TRTCAppSceneLIVE);
        resolve();
        uploadLiveLog({
          message: "Electron-TRTC调用进房",
          level: "info",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            roomId: this.options.roomId,
            userId: this.options.userId,
          },
        });
      } else {
        reject("enterRoom_no client");
        uploadLiveLog({
          message: "Electron-TRTC调用进房失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
          },
        });
      }
    });
  }
  exitRoom() {
    return new Promise((resolve, reject) => {
      if (this.client) {
        resolve(this.client.exitRoom());
        uploadLiveLog({
          message: "Electron-TRTC退出房间",
          level: "info",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
          },
        });
      } else {
        reject("exitRoom_no client");
        uploadLiveLog({
          message: "Electron-TRTC退出房间失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
          },
        });
      }
    });
  }
  // ----------------------------------------------------摄像头相关--------------------------------------
  /**
   * 获取摄像头列表
   * @returns {Promise<Array>} 返回摄像头列表
   * @property {Array} cameras - 摄像头列表
   * @property {string} cameras[].deviceId - 摄像头的ID
   * @property {string} cameras[].deviceName - 摄像头的名称
   * @property {string} cameras[].deviceProperties - 设备属性
   * @abstract
   */
  getCameraList() {
    return new Promise((resolve, reject) => {
      if (this.client) {
        try {
          const list = this.client.getCameraDevicesList();
          resolve(list);
          uploadLiveLog({
            message: "Electron-TRTC获取摄像头列表",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              list,
            },
          });
        } catch (error) {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC获取摄像头列表失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: error.message,
            },
          });
        }
      } else {
        reject("getCameraList_no client");
        uploadLiveLog({
          message: "Electron-TRTC获取摄像头列表失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
          },
        });
      }
    });
  }
  /**
   * 获取当前摄像头设备
   * @returns {Object || null} 返回当前摄像头设备
   * @property {string} deviceId - 摄像头的ID
   * @property {string} deviceName - 摄像头的名称
   * @abstract
   */
  getCurrentCameraDevice() {
    const videoDevice = this.client.getCurrentCameraDevice();
    console.log("currentvideoDevice", videoDevice);

    if (videoDevice) {
      uploadLiveLog({
        message: "Electron-TRTC获取当前摄像头设备",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          deviceId: videoDevice.deviceId,
        },
      });
      return {
        deviceId: videoDevice.deviceId,
        deviceName: videoDevice.deviceName,
        ...videoDevice,
      };
    } else {
      uploadLiveLog({
        message: "Electron-TRTC获取当前摄像头设备失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: "no device",
        },
      });
      return null;
    }
  }
  /**
   * 开始本地摄像头预览
   * @param {Object} params - 预览参数
   * @param {HTMLElement} params.dom - 预览的DOM元素
   * @param {boolean} params.publish - 是否发布
   * @returns {Promise<void>}
   * @abstract
   */
  startLocalPreview({ dom }) {
    console.log("djj_开启本地视频");
    const asyncFn = () => {
      return new Promise((resolve, reject) => {
        if (this.client) {
          try {
            if (dom) {
              this.renderCameraDom = dom;
            }
            if (this.videoState) {
              this.client.updateLocalView(this.renderCameraDom);
              resolve();
              uploadLiveLog({
                message: "Electron-TRTC开启本地视频--- 已开启",
                level: "info",
                tag: "api",
                extra: {
                  component: "ElectronTRTCService",
                  videoState: this.videoState,
                },
              });
              return;
            }
            console.log("djj_开启本地视频", this.renderCameraDom);
            this.client.startLocalPreview(this.renderCameraDom);
            // 初始化镜像
            this.initMirror("client");
            // 初始化美颜
            this.initBasicBeauty("client");
            this.videoState = true;
            resolve();
            uploadLiveLog({
              message: "Electron-TRTC开启本地视频",
              level: "info",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
              },
            });
          } catch (error) {
            reject(error);
            uploadLiveLog({
              message: "Electron-TRTC开启本地视频失败",
              level: "error",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
                error: error.message,
              },
            });
          }
        } else {
          reject("startLocalPreview_no client");
        }
      });
    };
    return this.queueAsyncVideo(asyncFn);
  }

  /**
   * 切换摄像头
   * @param {Object} params
   * @param {string} params.deviceId - 设备ID
   */
  setCurrentCameraDevice(params) {
    return new Promise((resolve, reject) => {
      const { deviceId } = params;
      if (this.client) {
        try {
          this.client.setCurrentCameraDevice(deviceId);
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC切换摄像头",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              deviceId,
            },
          });
        } catch (error) {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC切换摄像头失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              deviceId,
              error: error.message,
            },
          });
        }
      } else {
        reject("setCurrentCameraDevice_no client");
        uploadLiveLog({
          message: "Electron-TRTC切换摄像头失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            deviceId,
            error: "no client",
          },
        });
      }
    });
  }
  /**
   * 停止本地摄像头采集
   * @returns {Promise<void>}
   */
  stopLocalPreview() {
    console.log("djj_关闭本地视频");
    const asyncFn = () => {
      return new Promise((resolve, reject) => {
        if (this.client) {
          try {
            if (!this.videoState) {
              resolve();
              uploadLiveLog({
                message: "Electron-TRTC关闭本地视频-未开启",
                level: "info",
                tag: "api",
                extra: {
                  component: "ElectronTRTCService",
                  videoState: this.videoState,
                },
              });
              return;
            }
            this.client.stopLocalPreview();
            this.videoState = false;
            resolve();
            uploadLiveLog({
              message: "Electron-TRTC关闭本地视频",
              level: "info",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
              },
            });
          } catch (error) {
            reject(error);
            uploadLiveLog({
              message: "Electron-TRTC关闭本地视频失败",
              level: "error",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
                error: error.message,
              },
            });
          }
        } else {
          reject("stopLocalPreview_no client");
          uploadLiveLog({
            message: "Electron-TRTC关闭本地视频失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: "no client",
            },
          });
        }
      });
    };
    return this.queueAsyncVideo(asyncFn);
  }
  /**
   * 暂停或者恢复本地视频采集 手动关闭后必须在次调用打开才能恢复，否则不会推流
   * @param {Object} params
   * @param {boolean} params.mute
   * @returns {Promise<void>}
   */
  // muteLocalVideo(params) {
  //   console.log("djj_暂停或恢复", params);
  //   return new Promise((resolve, reject) => {
  //     const { mute, streamType } = params;
  //     if (this.client) {
  //       try {
  //         this.client.muteLocalVideo(mute, streamType);
  //         resolve();
  //       } catch (error) {
  //         reject(error);
  //       }
  //     } else {
  //       reject();
  //     }
  //   });
  // }
  /**
   * 开始显示远端视频画面
   * @param {*} params
   * @param {string} params.userID - 用户ID
   * @param {number} params.streamType - 视频流类型
   * @param  {HTMLElement} params.view - 视频的DOM元素
   */

  startRemoteView(params) {
    return new Promise((resolve, reject) => {
      if (this.client) {
        const { userID, streamType, view } = params;
        try {
          this.client.setRemoteRenderParams(userID, streamType, {
            fillMode: TRTCVideoFillMode.TRTCVideoFillMode_Fill,
          });
          this.client.startRemoteView(userID, view, streamType);
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC开始显示远端视频画面",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              userId: userID,
              streamType: streamType,
            },
          });
        } catch (error) {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC开始显示远端视频画面失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: error.message,
            },
          });
        }
      } else {
        reject("startRemoteView_no client");
        uploadLiveLog({
          message: "Electron-TRTC开始显示远端视频画面失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
          },
        });
      }
    });
  }
  /**
   * 开始显示远端辅路视频画面
   * @param {*} params
   * @param {string} params.userID - 用户ID
   * @param  {HTMLElement} params.view - 视频的DOM元素
   */

  startRemoteSubView(params = {}) {
    const { userID, view } = params;
    try {
      this.client.startRemoteView(
        userID,
        view,
        TRTCVideoStreamType.TRTCVideoStreamTypeSub
      );
      uploadLiveLog({
        message: "Electron-TRTC开始显示远端辅路视频画面成功",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      uploadLiveLog({
        message: "Electron-TRTC开始显示远端辅路视频画面失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  /**
   * 停止显示远端视频画面，同时不再拉取该远端用户的视频数据流。
   * @param {*} params
   * @param {string} params.userID - 用户ID
   * @param {number} params.streamType - 视频流类型

   */
  stopRemoteView(params = {}) {
    return new Promise((resolve, reject) => {
      if (this.client) {
        try {
          const { userID, streamType } = params;
          this.client.stopRemoteView(userID, streamType);
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC停止显示远端视频画面成功",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              params,
            },
          });
        } catch (error) {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC停止显示远端视频画面失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: error.message,
              params,
            },
          });
        }
      } else {
        reject("stopRemoteView_no client");
        uploadLiveLog({
          message: "Electron-TRTC停止显示远端视频画面失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
            params,
          },
        });
      }
    });
  }
  /**
   * 更新远端
   * @param {*} params
   * @param {string} params.userID - 用户ID
   * @param {number} params.streamType - 视频流类型
   * @param  {HTMLElement} params.view - 视频的DOM元素
   */

  updateRemoteVideo(params) {
    try {
      const { userID, streamType, view } = params;
      this.client.updateRemoteView(userID, view, streamType);
      uploadLiveLog({
        message: "Electron-TRTC更新远端视频画面成功",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          params,
        },
      });
    } catch (error) {
      uploadLiveLog({
        message: "Electron-TRTC更新远端视频画面失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error,
          params,
        },
      });
    }
  }

  /**
   * 测试实例本地摄像头预览
   * @param {Object} params - 预览参数
   * @param {HTMLElement} params.dom - 预览的DOM元素
   * @param {boolean} params.publish - 是否发布
   * @returns {Promise<void>}
   * @abstract
   */
  startLocalPreviewByTestClient({ dom }) {
    return new Promise((resolve, reject) => {
      try {
        const tag = store?.state?.live?.isMirror;
        this.testClient.startLocalPreview(dom);
        const localRenderParams = new TRTCRenderParams(
          TRTCVideoRotation.TRTCVideoRotation0,
          TRTCVideoFillMode.TRTCVideoFillMode_Fill,
          tag
            ? TRTCVideoMirrorType.TRTCVideoMirrorType_Enable
            : TRTCVideoMirrorType.TRTCVideoMirrorType_Disable
        );
        this.testClient.setLocalRenderParams(localRenderParams);
        // if (deviceId) {
        //   this.testClient.setCurrentCameraDevice(deviceId);
        // }
        resolve();
        uploadLiveLog({
          message: "Electron-TRTC测试实例开启摄像头预览",
          level: "info",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
          },
        });
        // if (!this.testClient) {
        //   // 创建子实例
        //   this.testClient = this.client.createSubCloud();
        //   this.testClient.startLocalPreview(dom);
        //   if (deviceId) {
        //     this.testClient.setCurrentCameraDevice(deviceId);
        //   }
        //   // 初始化镜像
        //   this.initMirror("testClient");
        //   // 初始化美颜
        //   this.initBasicBeauty("testClient");
        //   resolve();
        // } else {
        //   const tag = store?.state?.live?.isMirror;
        //   this.testClient.startLocalPreview(dom);
        //   const localRenderParams = new TRTCRenderParams(
        //     TRTCVideoRotation.TRTCVideoRotation0,
        //     TRTCVideoFillMode.TRTCVideoFillMode_Fill,
        //     tag
        //       ? TRTCVideoMirrorType.TRTCVideoMirrorType_Enable
        //       : TRTCVideoMirrorType.TRTCVideoMirrorType_Disable
        //   );
        //   this.testClient.setLocalRenderParams(localRenderParams);
        //   if (deviceId) {
        //     this.testClient.setCurrentCameraDevice(deviceId);
        //   }
        //   resolve();
        // }
      } catch (error) {
        reject(error);
        uploadLiveLog({
          message: "Electron-TRTC测试实例开启摄像头预览失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: error.message,
          },
        });
      }
    });
  }
  /**
   * 停止本地摄像头采集
   * @returns {Promise<void>}
   */
  stopLocalPreviewByTestClient() {
    return new Promise((resolve, reject) => {
      console.log("stopLocalPreviewByTestClient___", this.testClient);
      if (this.testClient) {
        try {
          this.testClient.stopLocalPreview();
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC测试实例停止摄像头预览",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
            },
          });
        } catch (error) {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC测试实例停止摄像头预览失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: error.message,
            },
          });
        }
      } else {
        reject("stopLocalPreviewByTestClient_no client");
        uploadLiveLog({
          message: "Electron-TRTC测试实例停止摄像头预览失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
          },
        });
      }
    });
  }
  /**
   * 切换摄像头
   * @param {Object} params
   * @param {string} params.deviceId - 设备ID
   * @returns {Promise<void>}
   */
  setCurrentCameraDeviceByTestClient(params) {
    return new Promise((resolve, reject) => {
      if (this.testClient) {
        const { deviceId } = params;
        try {
          this.testClient.setCurrentCameraDevice(deviceId);
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC测试实例切换摄像头",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              deviceId,
            },
          });
        } catch (error) {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC测试实例切换摄像头失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              deviceId,
              error: error.message,
            },
          });
        }
      } else {
        reject("setCurrentCameraDeviceByTestClient_no client");
        uploadLiveLog({
          message: "Electron-TRTC测试实例切换摄像头失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            deviceId: params.deviceId,
            error: "no client",
          },
        });
      }
    });
  }
  /**
   * 设置视频编码参数
   * @param {Object} params
   * @param {number} params.width - 视频宽度
   * @param {number} params.height - 视频高度
   * @param {number} params.fps - 视频帧率
   * @param {number} params.bitrate - 视频码率
   * @returns {Promise<void>}
   */
  setVideoEncoderParams() {
    console.log("setVideoEncoderParams___", 111);
    return new Promise((resolve, reject) => {
      if (this.client) {
        try {
          // TRTCVideoResolution_320_180   码率 400
          // TRTCVideoResolution_480_270   码率 550
          // TRTCVideoResolution_640_360   码率 900
          // TRTCVideoResolution_960_540   码率 1300
          const params = new TRTCVideoEncParam(
            this.videoProfile.resolution,
            TRTCVideoResolutionMode.TRTCVideoResolutionModeLandscape,
            this.videoProfile.fps,
            this.videoProfile.bitrate,
            this.videoProfile.minBitrate,
            false
          );
          this.client.setVideoEncoderParam(params);
          console.log("setVideoEncoderParams___", params);
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC设置视频编码参数成功",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
            },
          });
        } catch (error) {
          console.error("setVideoEncoderParams___", error);
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC设置视频编码参数失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
            },
          });
        }
      }
    });
  }
  // ----------------------------------------------------麦克风相关--------------------------------------
  /**
   * 初始化开启本地麦克风采集
   * @param {Object} params
   * @returns {Promise<void>}
   */
  initStartLocalAudio(params = {}) {
    const asyncFn = () => {
      return new Promise((resolve, reject) => {
        if (this.audioState) {
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC初始化开启本地麦克风采集成功--已经开启",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              audioState: this.audioState,
            },
          });
          return;
        }
        if (this.client) {
          console.log("startLocalAudio___startLocalAudio");
          try {
            this.client.startLocalAudio(
              TRTCAudioQuality.TRTCAudioQualityDefault
            );
            this.client.muteLocalAudio(true);
            this.audioState = true;
            resolve();
            uploadLiveLog({
              message: "Electron-TRTC初始化开启本地麦克风采集成功",
              level: "info",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
              },
            });
          } catch (error) {
            reject(error);
            uploadLiveLog({
              message: "Electron-TRTC初始化开启本地麦克风采集失败",
              level: "error",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
                error: error,
              },
            });
          }
        } else {
          reject();
          uploadLiveLog({
            message: "Electron-TRTC初始化开启本地麦克风采集失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: "no client",
            },
          });
        }
      });
    };
    return this.queueAsyncAudio(asyncFn);
  }
  /**
   * 获取麦克风列表
   * @returns {Promise<Array>} 麦克风列表
   * @property {Array} micro - 麦克风列表
   * @property {string} micro[].deviceId - 麦克风的ID
   * @property {string} micro[].deviceName - 麦克风的名称
   * @property {string} micro[].deviceProperties - 设备属性
   * @property {string} micro[].realDeviceId - 真实设备ID（Web API）
   */
  getMicrophoneList() {
    return new Promise((resolve, reject) => {
      if (!this.client) {
        reject("getMicrophoneList_no client");
        uploadLiveLog({
          message: "Electron-TRTC获取麦克风列表失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
          },
        });
        return;
      }
      const sdkList = this.client.getMicDevicesList();
      if (!sdkList) {
        console.error(
          "new_live_electron_getMicrophoneList",
          "获取麦克风列表失败"
        );
        reject("获取麦克风列表失败");
        uploadLiveLog({
          message: "Electron-TRTC获取麦克风列表失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no list",
          },
        });
        return;
      }

      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          const webList = devices.filter((d) => d.kind === "audioinput");
          function normalize(str) {
            return (str || "")
              .replace(/\s+/g, "")
              .replace(/\(.*?\)/g, "")
              .toLowerCase();
          }
          const mappedList = sdkList.map((sdkDev) => {
            const sdkName = normalize(sdkDev.deviceName);
            const match = webList.find((webDev) => {
              const webName = normalize(webDev.label);
              return webName.includes(sdkName) || sdkName.includes(webName);
            });
            return {
              ...sdkDev,
              realDeviceId: match ? match.deviceId : null,
              isDefault: match ? match.deviceId === "default" : false,
            };
          });
          console.log("new_live_electron_getMicrophoneList", mappedList);
          resolve(mappedList);
          uploadLiveLog({
            message: "Electron-TRTC获取麦克风列表成功",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              list: mappedList,
            },
          });
        })
        .catch((error) => {
          console.error("获取Web设备列表失败", error);
          // 如果Web API失败，仍然返回SDK列表
          resolve(sdkList);
        });
    });
  }
  /**
   * 开启本地麦克风采集，并发布到当前的房间中。
   * @returns {Promise<any>}
   */
  startLocalAudio() {
    const asyncFn = () => {
      return new Promise((resolve, reject) => {
        if (this.audioState) {
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC开启本地麦克风采集成功--已经开启",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              audioState: this.audioState,
            },
          });
          return;
        }
        if (this.client) {
          console.log("startLocalAudio___startLocalAudio");
          try {
            this.client.startLocalAudio(
              TRTCAudioQuality.TRTCAudioQualityDefault
            );
            this.audioState = true;
            resolve();
            uploadLiveLog({
              message: "Electron-TRTC开启本地麦克风采集成功",
              level: "info",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
              },
            });
          } catch (error) {
            reject(error);
            uploadLiveLog({
              message: "Electron-TRTC开启本地麦克风采集失败",
              level: "error",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
                error: error,
              },
            });
          }
        } else {
          reject();
          uploadLiveLog({
            message: "Electron-TRTC开启本地麦克风采集失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: "no client",
            },
          });
        }
      });
    };
    return this.queueAsyncAudio(asyncFn);
  }
  /**
   * 停止本地麦克风的采集及发布
   * @returns {Promise<any>}
   */
  stopLocalAudio() {
    const asyncFn = () => {
      return new Promise((resolve, reject) => {
        if (!this.audioState) {
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC停止本地麦克风采集成功--未开启",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              audioState: this.audioState,
            },
          });
          return;
        }
        if (this.client) {
          try {
            this.client.stopLocalAudio();
            this.audioState = false;
            resolve();
            uploadLiveLog({
              message: "Electron-TRTC停止本地麦克风采集成功",
              level: "info",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
              },
            });
          } catch (error) {
            reject(error);
            uploadLiveLog({
              message: "Electron-TRTC停止本地麦克风采集失败",
              level: "error",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
                error: error,
              },
            });
          }
        } else {
          reject();
          uploadLiveLog({
            message: "Electron-TRTC停止本地麦克风采集失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: "no client",
            },
          });
        }
      });
    };
    return this.queueAsyncAudio(asyncFn);
  }
  /**
   * 切换麦克风
   * @param {Object} params
   * @param {string} params.deviceId
   * @returns {Promise<void>}
   */
  setCurrentMicDevice(params) {
    const asyncFn = () => {
      return new Promise((resolve, reject) => {
        try {
          const { deviceId } = params;
          if (this.client) {
            this.client.setCurrentMicDevice(deviceId);
            resolve();
            uploadLiveLog({
              message: "Electron-TRTC切换麦克风",
              level: "info",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
                deviceId,
              },
            });
          } else {
            reject();
          }
        } catch (error) {
          console.error("new_live_electron_setCurrentMicDevice", error);
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC切换麦克风失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              deviceId: params.deviceId,
              error: error.message,
            },
          });
        }
      });
    };
    return this.queueAsyncAudio(asyncFn);
  }
  /**
   * 麦克风静音/开启
   * @param {Object} params
   * @param {boolean} params.mute
   */
  muteLocalAudio(params) {
    const asyncFn = () => {
      return new Promise((resolve, reject) => {
        const { mute } = params;
        if (this.client) {
          try {
            if (!this.audioState) {
              this.client.startLocalAudio();
              this.audioState = true;
            }
            const isScreenShare = ["start", "pause"].includes(
              store?.state?.live?.screenShareStatus
            );
            const isShareSysAudio =
              store?.state?.live?.screenSharePreInfo?.isAudio;
            if (isScreenShare && isShareSysAudio && mute) {
              this.client.setAudioCaptureVolume(0);
            } else {
              if (!mute) {
                const captureVolume = this.getAudioCaptureVolume();
                console.log("captureVolume>>", captureVolume);
                if (captureVolume === 0) {
                  this.client.setAudioCaptureVolume(100);
                }
              }
              this.client.muteLocalAudio(mute);
            }
            resolve();
            uploadLiveLog({
              message: `Electron-TRTC${mute ? "静音" : "取消静音"}本地音频`,
              level: "info",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
                mute,
              },
            });
            // updateState({
            //   audio_state: mute ? 2 : 1,
            //   room_id: this.options.roomId,
            // })
            //   .then((res) => {
            //     if (res.code === 1) {
            //       resolve();
            //     } else {
            //       this.$darkMessage.error(res.message);
            //       reject(res.message);
            //     }
            //   })
            //   .catch((error) => {
            //     reject(error);
            //   });
          } catch (error) {
            reject(error);
            uploadLiveLog({
              message: `Electron-TRTC${mute ? "静音" : "取消静音"}本地音频失败`,
              level: "error",
              tag: "api",
              extra: {
                component: "ElectronTRTCService",
                params,
                error: error.message,
              },
            });
          }
        } else {
          reject();
          uploadLiveLog({
            message: "Electron-TRTC静音本地音频失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: "no client",
            },
          });
        }
      });
    };
    return this.queueAsyncAudio(asyncFn);
  }
  /**
   * 静音某个远端用户的音频，只对当前用户有效
   * @param {Object} params
   * @param {string} params.userId
   * @param {boolean} params.mute
   */
  muteRemoteAudio(params) {
    return new Promise((resolve, reject) => {
      const { userId, mute } = params;
      if (this.client) {
        try {
          this.client.muteRemoteAudio(userId, mute);
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC静音远端音频成功",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              params,
            },
          });
        } catch (error) {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC静音远端音频失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: error,
              params,
            },
          });
        }
      } else {
        reject();
        uploadLiveLog({
          message: "Electron-TRTC静音远端音频失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
            params,
          },
        });
      }
    });
  }
  /**
   * 开启麦克风测试
   *@returns void
   */
  startMicDeviceTest(params) {
    try {
      const { interval, playback } = params;
      this.client.startMicDeviceTest(interval, playback);
      uploadLiveLog({
        message: "Electron-TRTC开启麦克风测试",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          interval,
          playback,
        },
      });
    } catch (error) {
      console.error("startMicDeviceTest___", error);
      uploadLiveLog({
        message: "Electron-TRTC开启麦克风测试失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  /**
   * 停止麦克风测试
   *@returns void
   */
  stopMicDeviceTest() {
    try {
      this.client.stopMicDeviceTest();
      uploadLiveLog({
        message: "Electron-TRTC停止麦克风测试",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("stopMicDeviceTest___", error);
      uploadLiveLog({
        message: "Electron-TRTC停止麦克风测试失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  /**
   * 开启音量检测
   * @param {Object} params
   * @param {number} params.interval
   */
  enableAudioVolumeEvaluation(params = {}) {
    try {
      const { interval = 300 } = params;
      this.client.enableAudioVolumeEvaluation(interval);
      uploadLiveLog({
        message: "Electron-TRTC开启音量检测",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          interval,
        },
      });
    } catch (error) {
      console.error("enableAudioVolumeEvaluation___", error);
      uploadLiveLog({
        message: "Electron-TRTC开启音量检测失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          interval: params.interval,
          error: error.message,
        },
      });
    }
  }
  // 获取当前选择的麦克风
  getCurrentMicDevice() {
    try {
      const device = this.client.getCurrentMicDevice();
      uploadLiveLog({
        message: "Electron-TRTC获取当前麦克风设备",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          deviceId: device.deviceId,
        },
      });
      return device;
    } catch (error) {
      console.error("getCurrentMicDevice___", error);
      uploadLiveLog({
        message: "Electron-TRTC获取当前麦克风设备失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  getCurrentMicDeviceMute() {
    try {
      const mute = this.client.getCurrentMicDeviceMute();
      uploadLiveLog({
        message: "Electron-TRTC获取当前麦克风设备静音状态",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          mute,
        },
      });
      return mute;
    } catch (error) {
      console.error("getCurrentMicDeviceMute___", error);
      uploadLiveLog({
        message: "Electron-TRTC获取当前麦克风设备静音状态失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
      return false;
    }
  }
  // ----------------------------------------------------扬声器相关--------------------------------------
  /**
   * 获取扬声器设备列表;
   * @returns {Promise<Array>}
   * @property {Array} speaker - 麦克风列表
   * @property {string} speaker[].deviceId - 麦克风的ID
   * @property {string} speaker[].deviceName - 麦克风的名称
   * @property {string} speaker[].deviceProperties - 设备属性
   *
   */
  getSpeakerDevicesList() {
    return new Promise((resolve, reject) => {
      if (!this.client) {
        reject("getSpeakerDevicesList_no client");
        uploadLiveLog({
          message: "Electron-TRTC获取扬声器列表失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
          },
        });
        return;
      }
      const sdkList = this.client.getSpeakerDevicesList();
      navigator.mediaDevices
        .enumerateDevices()
        .then((devices) => {
          const webList = devices.filter((d) => d.kind === "audiooutput");
          function normalize(str) {
            return (str || "")
              .replace(/\s+/g, "")
              .replace(/\(.*?\)/g, "")
              .toLowerCase();
          }
          const mappedList = sdkList.map((sdkDev) => {
            const sdkName = normalize(sdkDev.deviceName);
            const match = webList.find((webDev) => {
              const webName = normalize(webDev.label);
              return webName.includes(sdkName) || sdkName.includes(webName);
            });
            return {
              ...sdkDev,
              realDeviceId: match ? match.deviceId : null,
              isDefault: match ? match.deviceId === "default" : false,
            };
          });
          console.log("mappedList__", mappedList);

          resolve(mappedList);
          uploadLiveLog({
            message: "Electron-TRTC获取扬声器列表成功",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              count: mappedList.length,
            },
          });
        })
        .catch((error) => {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC获取扬声器列表失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: error.message,
            },
          });
        });
    });
  }
  /**
   * 切换扬声器
   * @param {Object} params
   * @param {string} params.deviceId
   * @returns {Promise<void>}
   */
  setCurrentSpeakerDevice(params) {
    return new Promise((resolve, reject) => {
      const { deviceId } = params;
      if (this.client) {
        try {
          console.log("device_speaker_设置扬声器ID", deviceId);
          this.client.setCurrentSpeakerDevice(deviceId);
          resolve();
          uploadLiveLog({
            message: "Electron-TRTC设置扬声器成功",
            level: "info",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              deviceId,
            },
          });
        } catch (error) {
          reject(error);
          uploadLiveLog({
            message: "Electron-TRTC设置扬声器失败",
            level: "error",
            tag: "api",
            extra: {
              component: "ElectronTRTCService",
              error: error.message,
              deviceId,
            },
          });
        }
      } else {
        reject("setCurrentSpeakerDevice_no client");
        uploadLiveLog({
          message: "Electron-TRTC设置扬声器失败",
          level: "error",
          tag: "api",
          extra: {
            component: "ElectronTRTCService",
            error: "no client",
            deviceId,
          },
        });
      }
    });
  }
  /**
   * 设置系统当前扬声器设备音量 设置的扬声器音量，范围0 - 100
   * @param {Object} params
   * @param {number} params.volume
   */
  setCurrentSpeakerVolume(params) {
    try {
      const { volume } = params;
      this.client.setCurrentSpeakerVolume(volume);
      uploadLiveLog({
        message: "Electron-TRTC设置扬声器音量",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          volume,
        },
      });
    } catch (error) {
      console.error("setCurrentSpeakerVolume___", error);
      uploadLiveLog({
        message: "Electron-TRTC设置扬声器音量失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          volume: params.volume,
          error: error.message,
        },
      });
    }
  }
  getCurrentSpeakerDeviceMute() {
    try {
      const mute = this.client.getCurrentSpeakerDeviceMute();
      uploadLiveLog({
        message: "Electron-TRTC获取当前扬声器设备静音状态",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          mute,
        },
      });
      return mute;
    } catch (error) {
      console.error("getCurrentSpeakerDeviceMute___", error);
      uploadLiveLog({
        message: "Electron-TRTC获取当前扬声器设备静音状态失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
      return false;
    }
  }
  // 获取 SDK 采集音量
  getAudioCaptureVolume() {
    try {
      const volume = this.client.getAudioCaptureVolume();
      uploadLiveLog({
        message: "Electron-TRTC获取SDK采集音量",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          volume,
        },
      });
      return volume;
    } catch (error) {
      console.error("getAudioCaptureVolume___", error);
      uploadLiveLog({
        message: "Electron-TRTC获取SDK采集音量失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
      return 0;
    }
  }
  // ----------------------------------------------------屏幕共享相关--------------------------------------
  /**
   * 启动屏幕共享
   * @returns {Promise<void>}
   */
  startScreenCapture(params) {
    try {
      const { view = null, win } = params;
      // TRTCVideoResolution_1920_1080   码率 2000
      // TRTCVideoResolution_1280_720   码率 1200
      // TRTCVideoResolution_960_540   码率 400 ;
      const screenShareEncParam = new TRTCVideoEncParam(
        this.screenShareProfile.resolution,
        TRTCVideoResolutionMode.TRTCVideoResolutionModeLandscape,
        this.screenShareProfile.fps,
        this.screenShareProfile.bitrate,
        this.screenShareProfile.minBitrate,
        false
      );
      console.log(view);
      this.client.startScreenCapture(
        view, // HTML Element
        TRTCVideoStreamType.TRTCVideoStreamTypeSub,
        screenShareEncParam
      );
      uploadLiveLog({
        message: "Electron-TRTC启动屏幕共享",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("startScreenCapture___", error);
      uploadLiveLog({
        message: "Electron-TRTC启动屏幕共享失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  /**
   * 枚举可共享的窗口列表
   * @returns {Promise<void>}
   */
  getScreenCaptureSources(params) {
    const { thumbHeight, thumbWidth, iconHeight, iconWidth } = params;
    return this.client.getScreenCaptureSources(
      thumbWidth,
      thumbHeight,
      iconWidth,
      iconHeight
    );
  }
  /**
   * 设置屏幕共享参数，该方法在屏幕共享过程中也可以调用
   * @returns {Promise<void>}
   */
  selectScreenCaptureTarget(params) {
    try {
      const { source, borderColor, borderWidth, property } = params;
      console.log("source____", source);
      const captureProperty = new TRTCScreenCaptureProperty(
        true, // enable capture mouse
        true, // enable highlight
        true, // enable high performance
        borderColor, // highlight color.
        borderWidth, // highlight width
        false // disable capture child window
      );
      let rect = new Rect();
      const selectRect = new Rect(0, 0, 0, 0);
      this.client.selectScreenCaptureTarget(
        source,
        selectRect,
        captureProperty
      );
      uploadLiveLog({
        message: "Electron-TRTC选择屏幕共享目标",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          sourceType: source.type,
          sourceId: source.sourceId,
        },
      });
    } catch (error) {
      console.error("selectScreenCaptureTarget___", error);
      uploadLiveLog({
        message: "Electron-TRTC选择屏幕共享目标失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  // 停止共享
  stopScreenCapture() {
    try {
      this.client.stopScreenCapture();
      uploadLiveLog({
        message: "Electron-TRTC停止屏幕共享",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("stopScreenCapture___", error);
      uploadLiveLog({
        message: "Electron-TRTC停止屏幕共享失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  // 暂停共享
  pauseScreenCapture() {
    try {
      this.client.pauseScreenCapture();
      uploadLiveLog({
        message: "Electron-TRTC暂停屏幕共享",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("pauseScreenCapture___", error);
      uploadLiveLog({
        message: "Electron-TRTC暂停屏幕共享失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  // 恢复共享
  resumeScreenCapture() {
    try {
      this.client.resumeScreenCapture();
      uploadLiveLog({
        message: "Electron-TRTC恢复屏幕共享",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("resumeScreenCapture___", error);
      uploadLiveLog({
        message: "Electron-TRTC恢复屏幕共享失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  // 不需要分享的窗口
  addExcludedShareWindow(win) {
    try {
      this.client.addExcludedShareWindow(win);
      uploadLiveLog({
        message: "Electron-TRTC添加排除共享窗口",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("addExcludedShareWindow___", error);
      uploadLiveLog({
        message: "Electron-TRTC添加排除共享窗口失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  onSystemAudioLoopbackError(errCode) {
    console.log("live_startSystemAudioLoopback__", errCode);
    if (errCode === 0) {
      console.log("启动成功");
      uploadLiveLog({
        message: "Electron-TRTC开启系统声音共享成功",
        level: "info",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          errCode,
        },
      });
    }
    if (errCode === -1330) {
      console.log("开启系统声音录制失败，例如音频驱动插件不可用");
      uploadLiveLog({
        message: "Electron-TRTC开启系统声音录制失败",
        level: "error",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          error: "音频驱动插件不可用",
          errCode,
        },
      });
    }
    if (errCode === -1331) {
      console.log("未授权安装音频驱动插件");
      uploadLiveLog({
        message: "Electron-TRTC未授权安装音频驱动插件",
        level: "error",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          error: "未授权安装音频驱动插件",
          errCode,
        },
      });
    }
    if (errCode === -1332) {
      console.log("安装音频驱动插件失败");
      uploadLiveLog({
        message: "Electron-TRTC安装音频驱动插件失败",
        level: "error",
        tag: "callback",
        extra: {
          component: "ElectronTRTCService",
          error: "安装音频驱动插件失败",
          errCode,
        },
      });
    }
  }
  // 开启系统声音共享
  startSystemAudioLoopback() {
    try {
      this.client.on(
        "onSystemAudioLoopbackError",
        this.onSystemAudioLoopbackError.bind(this)
      );
      this.client.startSystemAudioLoopback();
      uploadLiveLog({
        message: "Electron-TRTC调用开启系统声音共享",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("startSystemAudioLoopback___", error);
      uploadLiveLog({
        message: "Electron-TRTC调用开启系统声音共享失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  // 关闭系统声音共享
  stopSystemAudioLoopback() {
    try {
      this.client.stopSystemAudioLoopback();
      uploadLiveLog({
        message: "Electron-TRTC关闭系统声音共享",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("stopSystemAudioLoopback___", error);
      uploadLiveLog({
        message: "Electron-TRTC关闭系统声音共享失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }

  // ----------------------------------------------------消息相关--------------------------------------
  /**
   * 发送自定义消息
   * @param  {Object} params
   * @param  {Number} params.cmdId 消息 Id
   * @param  {String} params.data 消息内容
   */
  sendCustomMessage(params) {
    try {
      const { cmdId, data } = params;
      const res = this.client.sendCustomCmdMsg(
        cmdId,
        JSON.stringify(data),
        true,
        true
      );
      console.log("==55sendCustomMessage>>>>", res, params);
      uploadLiveLog({
        message: "Electron-TRTC发送自定义消息",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          cmdId,
          result: res,
        },
      });
    } catch (error) {
      console.error("sendCustomMessage___", error);
      uploadLiveLog({
        message: "Electron-TRTC发送自定义消息失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          cmdId: params.cmdId,
          error: error.message,
        },
      });
    }
  }

  //设置系统当前麦克风设备的音量。麦克风音量值，volume 范围0 - 100
  setCurrentMicDeviceVolume(volume) {
    try {
      this.client.setCurrentMicDeviceVolume(volume);
      uploadLiveLog({
        message: "Electron-TRTC设置麦克风音量",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          volume,
        },
      });
    } catch (error) {
      console.error("setCurrentMicDeviceVolume___", error);
      uploadLiveLog({
        message: "Electron-TRTC设置麦克风音量失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          volume,
          error: error.message,
        },
      });
    }
  }
  // 设置系统当前麦克风设备的静音状态;mute设置为 true 时，麦克风设备静音；设置为 false时，麦克风设备取消静音
  setCurrentMicDeviceMute(mute) {
    try {
      this.client.setCurrentMicDeviceMute(mute);
      uploadLiveLog({
        message: `Electron-TRTC设置麦克风设备${mute ? "静音" : "取消静音"}`,
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          mute,
        },
      });
    } catch (error) {
      console.error("setCurrentMicDeviceMute___", error);
      uploadLiveLog({
        message: `Electron-TRTC设置麦克风设备${mute ? "静音" : "取消静音"}失败`,
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          mute,
          error: error.message,
        },
      });
    }
  }

  //获取当前的扬声器设备
  getCurrentSpeakerDevice() {
    try {
      const device = this.client.getCurrentSpeakerDevice();
      uploadLiveLog({
        message: "Electron-TRTC获取当前扬声器设备",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          deviceId: device?.deviceId,
        },
      });
      return device;
    } catch (error) {
      console.error("getCurrentSpeakerDevice___", error);
      uploadLiveLog({
        message: "Electron-TRTC获取当前扬声器设备失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
      return null;
    }
  }
  // 获取系统当前扬声器设备音量;
  getCurrentSpeakerVolume() {
    try {
      const volume = this.client.getCurrentSpeakerVolume();
      uploadLiveLog({
        message: "Electron-TRTC获取扬声器音量",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          volume,
        },
      });
      return volume;
    } catch (error) {
      console.error("getCurrentSpeakerVolume___", error);
      uploadLiveLog({
        message: "Electron-TRTC获取扬声器音量失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
      return 0;
    }
  }

  // 设置系统当前扬声器设备的静音状态 mute设置为 true 时，扬声器设备静音；设置为 false时，扬声器设备取消静音
  setCurrentSpeakerDeviceMute(mute) {
    try {
      this.client.setCurrentSpeakerDeviceMute(mute);
      uploadLiveLog({
        message: `Electron-TRTC设置扬声器设备${mute ? "静音" : "取消静音"}`,
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          mute,
        },
      });
    } catch (error) {
      console.error("setCurrentSpeakerDeviceMute___", error);
      uploadLiveLog({
        message: `Electron-TRTC设置扬声器设备${mute ? "静音" : "取消静音"}失败`,
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          mute,
          error: error.message,
        },
      });
    }
  }

  setVideoMuteImage() {
    try {
      this.client.setVideoMuteImage();
      uploadLiveLog({
        message: "Electron-TRTC设置视频静音图片",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("setVideoMuteImage___", error);
      uploadLiveLog({
        message: "Electron-TRTC设置视频静音图片失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }

  //镜像
  setMirror(tag) {
    try {
      const localRenderParams = new TRTCRenderParams(
        TRTCVideoRotation.TRTCVideoRotation0,
        TRTCVideoFillMode.TRTCVideoFillMode_Fill,
        tag
          ? TRTCVideoMirrorType.TRTCVideoMirrorType_Enable
          : TRTCVideoMirrorType.TRTCVideoMirrorType_Disable
      );
      this.client.setLocalRenderParams(localRenderParams);
      this.client.setVideoEncoderMirror(tag);
      if (this.testClient) {
        this.testClient.setLocalRenderParams(localRenderParams);
        this.testClient.setVideoEncoderMirror(tag);
      }
      store.commit("live/setIsMirror", tag);
      uploadLiveLog({
        message: `Electron-TRTC${tag ? "开启" : "关闭"}镜像`,
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          mirror: tag,
        },
      });
    } catch (error) {
      console.error("setMirror___", error);
      uploadLiveLog({
        message: `Electron-TRTC设置镜像失败`,
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          mirror: tag,
          error: error.message,
        },
      });
    }
  }

  // 开启美颜
  startBasicBeauty({ beauty, brightness, ruddy }) {
    try {
      const params = [
        TRTCBeautyStyle.TRTCBeautyStyleNature,
        beauty / 10,
        brightness / 10,
        ruddy / 10,
      ];
      this.client.setBeautyStyle(...params);
      if (this.testClient) {
        this.testClient.setBeautyStyle(...params);
      }
      store.commit("live/setOpenBeauty", true);
      uploadLiveLog({
        message: "Electron-TRTC开启美颜",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          beauty,
          brightness,
          ruddy,
        },
      });
    } catch (error) {
      console.error("startBasicBeauty___", error);
      uploadLiveLog({
        message: "Electron-TRTC开启美颜失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          beauty,
          brightness,
          ruddy,
          error: error.message,
        },
      });
    }
  }
  //关闭美颜
  stopBasicBeauty() {
    try {
      const params = [TRTCBeautyStyle.TRTCBeautyStyleNature, 0, 0, 0];
      this.client.setBeautyStyle(...params);
      if (this.testClient) {
        this.testClient.setBeautyStyle(...params);
      }
      store.commit("live/setOpenBeauty", false);
      uploadLiveLog({
        message: "Electron-TRTC关闭美颜",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
        },
      });
    } catch (error) {
      console.error("stopBasicBeauty___", error);
      uploadLiveLog({
        message: "Electron-TRTC关闭美颜失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
  //更新美颜
  updateBasicBeauty({ beauty, brightness, ruddy }) {
    try {
      const params = [
        TRTCBeautyStyle.TRTCBeautyStyleNature,
        beauty / 10,
        brightness / 10,
        ruddy / 10,
      ];
      this.client.setBeautyStyle(...params);
      if (this.testClient) {
        this.testClient.setBeautyStyle(...params);
      }
      store.commit("live/setOpenBeauty", true);
      store.commit("live/setBeautyOptions", { beauty, brightness, ruddy });
    } catch (error) {
      console.error("updateBasicBeauty___", error);
    }
  }
  //初始化美颜
  initBasicBeauty(client) {
    try {
      if (store?.state?.live?.openBeauty) {
        const { beauty, brightness, ruddy } = store?.state?.live?.beautyOptions;
        this[client].setBeautyStyle(
          TRTCBeautyStyle.TRTCBeautyStyleNature,
          beauty / 10,
          brightness / 10,
          ruddy / 10
        );
      }
    } catch (error) {
      console.error("initBasicBeauty___", error);
    }
  }
  // 初始化镜像
  initMirror(client) {
    try {
      const tag = store?.state?.live?.isMirror;
      const localRenderParams = new TRTCRenderParams(
        TRTCVideoRotation.TRTCVideoRotation0,
        TRTCVideoFillMode.TRTCVideoFillMode_Fill,
        tag
          ? TRTCVideoMirrorType.TRTCVideoMirrorType_Enable
          : TRTCVideoMirrorType.TRTCVideoMirrorType_Disable
      );
      this[client].setLocalRenderParams(localRenderParams);
      this[client].setVideoEncoderMirror(tag);
    } catch (error) {
      console.error("initMirror___", error);
    }
  }

  // 控制远端音频的播放音量
  setRemoteAudioVolume({ userId = "*", volume = 100 }) {
    try {
      this.client.setRemoteAudioVolume(userId, volume);
      uploadLiveLog({
        message: "Electron-TRTC设置远端音频音量",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          userId,
          volume,
        },
      });
    } catch (error) {
      console.error("setRemoteAudioVolume___", error);
      uploadLiveLog({
        message: "Electron-TRTC设置远端音频音量失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }

  // 切换用户角色，仅在 TRTC.TYPE.SCENE_LIVE 互动直播模式下生效。
  switchRole(role, option = {}) {
    try {
      this.client.switchRole(role, option);
      uploadLiveLog({
        message: "Electron-TRTC切换用户角色",
        level: "info",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          role,
        },
      });
    } catch (error) {
      console.error("switchRole___", error);
      uploadLiveLog({
        message: "Electron-TRTC切换用户角色失败",
        level: "error",
        tag: "api",
        extra: {
          component: "ElectronTRTCService",
          error: error.message,
        },
      });
    }
  }
}

export default TRTCLiveService;
